import { useState } from "react";
import { useApi } from "@/lib/useApi";
import { api, apiErr, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PageHeader, EmptyState } from "@/components/common";
import { ShieldQuestion, Plus, Paperclip, Loader2, X, Send, CheckCircle2, RotateCcw, FileText, ShieldAlert } from "lucide-react";
import { fDateTime } from "@/lib/format";
import { toast } from "sonner";

const CATS = [
  { v: "suspicious_mail", l: "Osäkert mejl", icon: ShieldAlert, cls: "bg-red-100 text-red-800" },
  { v: "question", l: "Fråga", icon: ShieldQuestion, cls: "bg-sky-100 text-sky-800" },
  { v: "access", l: "Behörighet/åtkomst", cls: "bg-amber-100 text-amber-800" },
  { v: "other", l: "Övrigt", cls: "bg-slate-100 text-slate-700" },
];
const catOf = (v) => CATS.find((c) => c.v === v) || CATS[3];

function AttachButton({ onUploaded, testid }) {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/uploads/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded({ path: r.data.path, name: r.data.name });
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border px-3 py-2 text-sm hover:bg-secondary" data-testid={testid}>
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />} Bifoga
      <input type="file" accept="application/pdf,image/*" className="hidden" onChange={onFile} />
    </label>
  );
}

function NewTicket({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ category: "suspicious_mail", subject: "", body: "" });
  const [att, setAtt] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.subject.trim()) return toast.error("Ange ett ämne");
    setBusy(true);
    try {
      await api.post("/it/tickets", { ...f, attachment_path: att?.path || null, attachment_name: att?.name || null });
      toast.success("Skickat till IT/Admin");
      setF({ category: "suspicious_mail", subject: "", body: "" }); setAtt(null); setOpen(false); onCreated();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button onClick={() => setOpen(true)} data-testid="it-new-btn"><Plus className="mr-1 h-4 w-4" />Fråga IT/Admin</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="it-new-dialog">
          <DialogHeader><DialogTitle>Fråga IT/Admin eller flagga osäkert mejl</DialogTitle>
            <DialogDescription>Beskriv ärendet så återkommer IT/Admin. Är du osäker på ett mejl? Bifoga gärna en skärmdump.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Typ av ärende</Label>
              <Select value={f.category} onValueChange={(v) => setF((s) => ({ ...s, category: v }))}>
                <SelectTrigger data-testid="it-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>Ämne</Label><Input value={f.subject} onChange={(e) => setF((s) => ({ ...s, subject: e.target.value }))} placeholder="t.ex. Misstänkt mejl om lösenord" data-testid="it-subject" /></div>
            <div><Label>Beskrivning</Label><Textarea rows={4} value={f.body} onChange={(e) => setF((s) => ({ ...s, body: e.target.value }))} placeholder="Beskriv vad som hänt. Osäker? Bifoga gärna en skärmdump." data-testid="it-body" /></div>
            <div className="flex items-center gap-2">
              <AttachButton onUploaded={setAtt} testid="it-attach" />
              {att && <span className="inline-flex items-center gap-1 rounded bg-muted px-2 py-1 text-xs"><FileText className="h-3.5 w-3.5" />{att.name}<button onClick={() => setAtt(null)}><X className="h-3.5 w-3.5" /></button></span>}
              <Button className="ml-auto" onClick={submit} disabled={busy} data-testid="it-submit"><Send className="mr-1 h-4 w-4" />Skicka</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function TicketThread({ id, isAdmin, onChanged, onClose }) {
  const { data: t, reload } = useApi(id ? `/it/tickets/${id}` : null, [id]);
  const [msg, setMsg] = useState("");
  const [att, setAtt] = useState(null);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try {
      await api.post(`/it/tickets/${id}/messages`, { body: msg, attachment_path: att?.path || null, attachment_name: att?.name || null });
      setMsg(""); setAtt(null); reload(); onChanged();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const setStatus = async (status) => { try { await api.patch(`/it/tickets/${id}`, { status }); reload(); onChanged(); } catch (e) { toast.error(apiErr(e)); } };
  if (!t) return null;
  const cat = catOf(t.category);
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="it-thread">
        <DialogHeader><DialogTitle className="flex items-center gap-2 pr-6"><Badge className={cat.cls}>{cat.l}</Badge><span className="truncate">{t.subject}</span></DialogTitle>
          <DialogDescription className="sr-only">Ärendetråd mellan medarbetare och IT/Admin.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[55vh] space-y-3 overflow-y-auto pr-1">
          <div className="rounded-lg bg-secondary p-3 text-sm">
            <div className="mb-1 text-xs text-muted-foreground">{t.created_by_name} · {fDateTime(t.created_at)}</div>
            {t.body || <span className="text-muted-foreground">(ingen beskrivning)</span>}
            {t.attachment_path && <a href={`${API}/files/${t.attachment_path}`} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-accent underline"><FileText className="h-3.5 w-3.5" />{t.attachment_name || "bilaga"}</a>}
          </div>
          {(t.messages || []).map((m, i) => (
            <div key={i} className={`rounded-lg p-3 text-sm ${m.is_admin ? "ml-6 bg-accent/10" : "mr-6 bg-secondary"}`} data-testid={`it-msg-${i}`}>
              <div className="mb-1 text-xs text-muted-foreground">{m.by_name}{m.is_admin ? " (IT/Admin)" : ""} · {fDateTime(m.created_at)}</div>
              {m.body}
              {m.attachment_path && <a href={`${API}/files/${m.attachment_path}`} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-accent underline"><FileText className="h-3.5 w-3.5" />{m.attachment_name || "bilaga"}</a>}
            </div>
          ))}
        </div>
        <div className="space-y-2 border-t pt-3">
          <Textarea rows={2} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Skriv ett svar…" data-testid="it-reply-input" />
          <div className="flex items-center gap-2">
            <AttachButton onUploaded={setAtt} testid="it-reply-attach" />
            {att && <span className="text-xs">{att.name}</span>}
            <div className="ml-auto flex gap-2">
              {isAdmin && (t.status === "open"
                ? <Button variant="outline" size="sm" onClick={() => setStatus("resolved")} data-testid="it-resolve"><CheckCircle2 className="mr-1 h-4 w-4" />Markera löst</Button>
                : <Button variant="outline" size="sm" onClick={() => setStatus("open")} data-testid="it-reopen"><RotateCcw className="mr-1 h-4 w-4" />Återöppna</Button>)}
              <Button size="sm" onClick={send} disabled={busy} data-testid="it-reply-send"><Send className="mr-1 h-4 w-4" />Svara</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ITSupport() {
  const { data, reload } = useApi("/it/tickets");
  const [sel, setSel] = useState(null);
  const tickets = data?.tickets || [];
  const isAdmin = !!data?.is_admin;
  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="IT & Admin" icon={ShieldQuestion} subtitle={isAdmin ? "Ärenden från medarbetare – svara och stäng. Osäkra mejl flaggas tydligt." : "Osäker på ett mejl eller behöver hjälp? Fråga IT/Admin här – bifoga gärna en skärmdump."}>
        <NewTicket onCreated={reload} />
      </PageHeader>

      {tickets.length === 0 ? (
        <EmptyState icon={ShieldQuestion} title="Inga ärenden" text="Skapa ett ärende när du är osäker på något – t.ex. ett mejl du inte litar på." />
      ) : (
        <div className="space-y-2">
          {tickets.map((t) => {
            const cat = catOf(t.category);
            return (
              <Card key={t.id} className={`flex cursor-pointer items-center gap-3 p-4 transition-colors hover:bg-secondary/50 ${t.category === "suspicious_mail" && t.status === "open" ? "border-red-200" : ""}`} onClick={() => setSel(t.id)} data-testid={`it-ticket-${t.id}`}>
                <Badge className={cat.cls}>{cat.l}</Badge>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{t.subject}</div>
                  <div className="truncate text-xs text-muted-foreground">{isAdmin ? `${t.created_by_name} · ` : ""}{(t.messages || []).length} svar · {fDateTime(t.updated_at)}</div>
                </div>
                <Badge variant={t.status === "resolved" ? "secondary" : "default"} data-testid={`it-status-${t.id}`}>{t.status === "resolved" ? "Löst" : "Öppet"}</Badge>
              </Card>
            );
          })}
        </div>
      )}
      {sel && <TicketThread id={sel} isAdmin={isAdmin} onChanged={reload} onClose={() => setSel(null)} />}
    </div>
  );
}
